import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useId, useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { type Naturaleza, naturalezaDe } from "./naturaleza"
import type { Linea } from "./tipos"

type Categoria = { id: number; nombre: string; activo: boolean }
type ArticuloBreve = { id: number; codigo: string; nombre: string }

const OPCIONES: { valor: Exclude<Naturaleza, "SUELDO">; etiqueta: string }[] = [
  { valor: "SERVICIO", etiqueta: "Servicio" },
  { valor: "MATERIAL", etiqueta: "Material de uso inmediato" },
  { valor: "CONSUMIBLE", etiqueta: "Consumible" },
  { valor: "BIEN", etiqueta: "Bien" },
]

/**
 * Qué ES un renglón y a qué partida va: su naturaleza y su categoría de gasto.
 *
 * ⚠ **Existe para corregir lo que el lector clasificó mal sin rehacer la factura**
 * (07/10): una factura de papelería entera había quedado como «Servicio», y sin esto
 * la única salida era borrar y volver a teclear veinte renglones.
 *
 * ⚠ **La categoría es POR RENGLÓN**: oficina y limpieza en la misma factura consumen
 * dos partidas. «La de la orden» es no tener una propia, no copiar la de hoy.
 *
 * No guarda nada: entrega los cambios a `EditarLinea`, que los manda con el resto. Un
 * `None` en el servidor es «no lo toques», y por eso quitar va con su bandera.
 */
export function ClasificarLinea({
  linea,
  onCambio,
}: {
  linea: Linea
  onCambio: (cambios: Record<string, unknown>) => void
}) {
  const id = useId()
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const inicial = naturalezaDe(linea)
  const [naturaleza, setNaturaleza] = useState<Naturaleza>(inicial)
  const [articuloId, setArticuloId] = useState<number | null>(linea.articulo_id)
  const [categoriaId, setCategoriaId] = useState<number | null>(linea.categoria_id ?? null)

  const esArticulo = naturaleza === "CONSUMIBLE" || naturaleza === "BIEN"
  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<Categoria[]>("/api/v1/categorias-gasto"),
  })
  const articulos = useQuery({
    queryKey: ["articulos", naturaleza],
    queryFn: () => api.get<ArticuloBreve[]>(`/api/v1/articulos?clase=${naturaleza}`),
    enabled: esArticulo && tienePermiso(usuario, "inventario:ver"),
  })
  const crear = useMutation({
    mutationFn: () =>
      api.post<ArticuloBreve>("/api/v1/articulos", {
        nombre: linea.descripcion.slice(0, 200),
        clase: naturaleza,
        ...(categoriaId !== null ? { categoria_id: categoriaId } : {}),
      }),
    onSuccess: (nuevo) => {
      setArticuloId(nuevo.id)
      clienteQuery.invalidateQueries({ queryKey: ["articulos"] })
    },
  })

  // Lo que cambió respecto de la línea, en el idioma del servidor.
  useEffect(() => {
    const cambios: Record<string, unknown> = {}
    if (naturaleza !== inicial || articuloId !== linea.articulo_id) {
      if (esArticulo) {
        if (articuloId !== null) cambios.articulo_id = articuloId
      } else {
        if (linea.articulo_id !== null) cambios.quitar_articulo = true
        cambios.uso_inmediato = naturaleza === "MATERIAL"
      }
    }
    if (categoriaId !== (linea.categoria_id ?? null)) {
      if (categoriaId === null) cambios.quitar_categoria = true
      else cambios.categoria_id = categoriaId
    }
    onCambio(cambios)
  }, [naturaleza, articuloId, categoriaId, esArticulo, inicial, linea, onCambio])

  if (inicial === "SUELDO") return null

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <label className="grid gap-1 text-xs">
        Qué es
        <select
          className="campo"
          value={naturaleza}
          onChange={(e) => {
            setNaturaleza(e.target.value as Naturaleza)
            setArticuloId(null)
          }}
        >
          {OPCIONES.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </select>
      </label>
      <label className="grid gap-1 text-xs">
        Categoría de gasto (partida)
        <select
          className="campo"
          value={categoriaId ?? ""}
          onChange={(e) => setCategoriaId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">La de la orden</option>
          {(categorias.data ?? [])
            .filter((c) => c.activo || c.id === categoriaId)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
        </select>
      </label>
      {esArticulo && (
        <div className="grid gap-1 sm:col-span-2">
          <label htmlFor={`${id}-articulo`} className="text-xs">
            {naturaleza === "BIEN" ? "Bien del depósito" : "Consumible del depósito"}
          </label>
          <SelectorBuscable
            id={`${id}-articulo`}
            opciones={(articulos.data ?? []).map((a) => ({
              valor: a.id,
              etiqueta: a.nombre,
              detalle: a.codigo,
            }))}
            valor={articuloId}
            onCambiar={setArticuloId}
            vacio="Elige el artículo"
            placeholder="Nombre o código…"
          />
          {articuloId === null && tienePermiso(usuario, "inventario:administrar") && (
            <Boton
              tamano="sm"
              className="justify-self-start"
              disabled={crear.isPending}
              onClick={() => crear.mutate()}
            >
              No existe: crearlo con el nombre del renglón
            </Boton>
          )}
          {articuloId === null && (
            <p className="text-xs" style={{ color: "var(--warning)" }}>
              Sin artículo elegido, el renglón no cambia de naturaleza.
            </p>
          )}
          {crear.error && (
            <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
              {crear.error.message}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
